import type { SkinGroup, TemplateModel } from '../api/types';
import { cleanName, count } from '../lib/format';
import { Discount } from './Discount';
import { useCurrency } from '../hooks/useCurrency';
import { useI18n } from '../i18n';

interface GroupProps {
  group: SkinGroup;
  onClick: () => void;
}

export function SkinCard({ group, onClick }: GroupProps) {
  const { money } = useCurrency();
  const { t, loc } = useI18n();
  const rarityColor = group.rarity?.color ?? '#555';
  return (
    <button type="button" className="card" onClick={onClick}>
      <div className="imgwrap">
        <div className="badges">
          {group.rarity && (
            <span className="badge" style={{ color: rarityColor }}>
              {loc(group.rarity)}
            </span>
          )}
          {group.isStatTrak && <span className="badge st">ST™</span>}
          {group.isSouvenir && <span className="badge sv">Souvenir</span>}
        </div>
        <SkinImage url={group.iconUrl} alt={group.name} />
        {group.rarity?.color && <i className="rarity-strip" style={{ background: group.rarity.color }} />}
      </div>

      <div className="body">
        <div className="name">{cleanName(group.name)}</div>
        <div className="variants">
          {t(group.variantCount > 1 ? 'level2.wearsCountPlural' : 'level2.wearsCount', { n: group.variantCount })}
        </div>
        <div className="meta">
          <span>🛒 {t('level2.onSale', { n: count(group.totalOnSale) })}</span>
        </div>
        <div className="prices">
          <div className="yp">
            <small>{t('level2.fromPrice')}</small>
            {money(group.minPrice)}
          </div>
          <div className="steam">
            {t('common.steam')} {money(group.minSteamPrice)}
            <br />
            <Discount value={group.bestDiscountPct} />
          </div>
        </div>
      </div>
    </button>
  );
}

interface TemplateProps {
  template: TemplateModel;
  onClick: () => void;
}

/** Card de uma variação específica (usado quando o agrupamento está desligado). */
export function TemplateCard({ template, onClick }: TemplateProps) {
  const { money } = useCurrency();
  const { t, loc } = useI18n();
  return (
    <button type="button" className="card" onClick={onClick}>
      <div className="imgwrap">
        <div className="badges">
          {template.exterior && (
            <span className="badge wear" style={{ background: wearColor(template.exterior.code) }}>
              {loc(template.exterior)}
            </span>
          )}
          {template.isStatTrak && <span className="badge st">ST™</span>}
        </div>
        {template.rarity?.color && <i className="rarity-strip" style={{ background: template.rarity.color }} />}
        <SkinImage url={template.iconUrl} alt={template.name} />
      </div>
      <div className="body">
        <div className="name">{cleanName(template.name)}</div>
        <div className="variants" style={{ color: template.exterior?.color ?? 'var(--muted)' }}>
          {loc(template.exterior)}
        </div>
        <div className="meta">
          <span>🛒 {count(template.onSaleCount)}</span>
        </div>
        <div className="prices">
          <div className="yp">
            {money(template.price)}
          </div>
          <div className="steam">
            {t('common.steam')} {money(template.steamPrice)}
            <br />
            <Discount value={template.discountPct} />
          </div>
        </div>
      </div>
    </button>
  );
}

/** Cores oficiais dos desgastes do CS2, iguais às da barra de filtros. */
function wearColor(code: string): string {
  switch (code) {
    case 'FN':
      return '#4bb14b';
    case 'MW':
      return '#87c542';
    case 'FT':
      return '#e6b83c';
    case 'WW':
      return '#e08a3c';
    case 'BS':
      return '#e05a48';
    default:
      return '#5b6673';
  }
}

export function SkinImage({ url, alt }: { url: string | null; alt: string }) {
  const { t } = useI18n();
  if (!url) return <div className="noimg">{t('common.noImage')}</div>;
  return (
    <img
      loading="lazy"
      src={url}
      alt={alt}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
}
